import {monthLabel} from '@vat/engine/month';
import {round2} from '@vat/engine/rounding';
import type {LandlordTaxProfile, VatEngineResult} from '@vat/engine/types';
import {VAT_EXPORT_COPY, VAT_TAX_STATUS_LABELS} from '@vat/vat-copy';
import {VatExportLandlord, VatExportPropertyLabel} from './vat-export.types';
import {formatDateDE, formatFullEuro, steuernummerLabel} from './vat-format';
import {propertyLabel} from './vat-pdf-vordruck';

/**
 * Kz-48-Jahresnachweis (Spec 6 „Ruhiger Modus“, Spec 7): steuerfreie Umsätze nach § 4 Nr. 12 UStG je Monat
 * und je Immobilie. Ohne VatReturn-Dokument – Download/Dateimanager, kein Storage, keine exports[] (A4).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export interface Kz48MonthRow {
    monthKey: string;
    kz48: number;
    lineCount: number;
    byProperty: Record<string, number>;
}

export interface Kz48NachweisInput {
    landlord: VatExportLandlord;
    taxProfile: LandlordTaxProfile;
    year: number;
    months: Kz48MonthRow[];
    properties: VatExportPropertyLabel[];
    engineVersion: string;
    now: Date;
}

const COLOR_MUTED = '#6b7280';
const COLOR_HEADER_FILL = '#f3f4f6';
const FONT_SMALL = 8;

/** Monatszeile: Kz 48 aus den Kennzahlen, Objektanteile als Σ round2(net × share) der Kz-48-Zeilen. */
export const buildKz48MonthRow = (monthKey: string, result: Pick<VatEngineResult, 'kennzahlen' | 'lines'>): Kz48MonthRow => {
    const lines = result.lines.filter(line => line.kz === '48');
    const byProperty: Record<string, number> = {};
    lines.forEach(line => {
        byProperty[line.source.propertyID] = round2((byProperty[line.source.propertyID] ?? 0) + round2(line.net * line.share));
    });
    return {monthKey, kz48: result.kennzahlen.kz48, lineCount: lines.length, byProperty};
};

export const kz48Total = (input: Kz48NachweisInput): number => round2(input.months.reduce((acc, m) => acc + m.kz48, 0));

/** Nur Immobilien mit Jahressumme ≠ 0, absteigend nach Summe, dann alphabetisch. */
export const summarizeKz48ByProperty = (input: Kz48NachweisInput): Array<{ id: string; label: string; sum: number }> => {
    const sums = new Map<string, number>();
    input.months.forEach(month => Object.entries(month.byProperty).forEach(([id, value]) => sums.set(id, round2((sums.get(id) ?? 0) + value))));
    return [...sums.entries()]
        .filter(([, sum]) => sum !== 0)
        .map(([id, sum]) => ({id, label: propertyLabel(input.properties, id), sum}))
        .sort((a, b) => b.sum - a.sum || a.label.localeCompare(b.label, 'de'));
};

export const buildKz48FooterText = (input: Kz48NachweisInput): string =>
    VAT_EXPORT_COPY.kz48.footer({datum: formatDateDE(input.now), engineVersion: input.engineVersion});

const headerCell = (text: string, alignment: 'left' | 'right' = 'left'): any => ({text, bold: true, fontSize: FONT_SMALL, fillColor: COLOR_HEADER_FILL, alignment, margin: [2, 3, 2, 3]});
const cell = (text: string, options: Record<string, unknown> = {}): any => ({text, fontSize: FONT_SMALL, margin: [2, 2, 2, 2], ...options});

/** pdfmake-Dokumentdefinition; Seite ohne Hintergrund (kein Briefpapier), Knoten-IDs kz48-header/-months/-properties. */
export const buildVatKz48DocDefinition = (input: Kz48NachweisInput): any => {
    const footerText = buildKz48FooterText(input);
    const monthsBody: any[][] = [
        [headerCell('Monat'), headerCell('Steuerfreie Umsätze (Kz 48, volle €)', 'right'), headerCell('Herleitungszeilen', 'right')],
        ...input.months.map(m => [cell(monthLabel(m.monthKey)), cell(formatFullEuro(m.kz48), {alignment: 'right'}), cell(String(m.lineCount), {alignment: 'right'})]),
        [cell(VAT_EXPORT_COPY.kz48.total, {bold: true}), cell(formatFullEuro(kz48Total(input)), {alignment: 'right', bold: true}), cell(String(input.months.reduce((a, m) => a + m.lineCount, 0)), {alignment: 'right', bold: true})],
    ];
    const properties = summarizeKz48ByProperty(input);
    const propertiesBody: any[][] = [
        [headerCell('Immobilie'), headerCell('Steuerfreie Umsätze (volle €)', 'right')],
        ...properties.map(p => [cell(p.label), cell(formatFullEuro(p.sum), {alignment: 'right'})]),
    ];

    return {
        info: {title: `Kz-48-Nachweis ${input.year} – ${input.landlord.name}`, author: 'Nahaus'},
        pageSize: 'A4',
        pageMargins: [40, 50, 40, 60],
        content: [
            {
                id: 'kz48-header',
                stack: [
                    {text: VAT_EXPORT_COPY.kz48.title(input.year), bold: true, fontSize: 16},
                    {text: VAT_EXPORT_COPY.kz48.subtitle, color: COLOR_MUTED, fontSize: 9},
                    {
                        table: {
                            widths: [150, '*'],
                            body: [
                                [cell('Eigentümer', {color: COLOR_MUTED}), cell([input.landlord.name, input.landlord.addressLine].filter(Boolean).join(', '))],
                                [cell('Einordnung', {color: COLOR_MUTED}), cell(VAT_TAX_STATUS_LABELS[input.taxProfile.status])],
                                [cell('Steuernummer', {color: COLOR_MUTED}), cell(steuernummerLabel(input.taxProfile))],
                                [cell('Kalenderjahr', {color: COLOR_MUTED}), cell(String(input.year))],
                            ],
                        },
                        layout: 'lightHorizontalLines',
                        margin: [0, 10, 0, 12],
                    },
                ],
            },
            {text: VAT_EXPORT_COPY.kz48.monthsTable, bold: true, fontSize: 11, margin: [0, 4, 0, 6]},
            {id: 'kz48-months', table: {headerRows: 1, widths: ['*', 170, 110], body: monthsBody}, layout: 'lightHorizontalLines', margin: [0, 0, 0, 12]},
            {text: VAT_EXPORT_COPY.kz48.propertiesTable, bold: true, fontSize: 11, margin: [0, 4, 0, 6]},
            properties.length
                ? {id: 'kz48-properties', table: {headerRows: 1, widths: ['*', 170], body: propertiesBody}, layout: 'lightHorizontalLines'}
                : {id: 'kz48-properties', text: VAT_EXPORT_COPY.pdf.none, fontSize: FONT_SMALL, color: COLOR_MUTED},
        ],
        footer: (currentPage: number, pageCount: number): any => ({
            stack: [
                {text: footerText, fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [40, 0, 40, 0]},
                {text: VAT_EXPORT_COPY.pdf.page(currentPage, pageCount), fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [0, 2, 0, 0]},
            ],
        }),
        defaultStyle: {fontSize: 9},
    };
};
